import React from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { useCurrentTracklog, useCurrentLogbook } from 'ff/debrief/hooks';
import LogbookEntryHeader from 'ff/debrief/components/logbookAssociation/LogbookEntryHeader';
import LogbookEntryDetails from 'ff/debrief/components/logbookAssociation/LogbookEntryDetails';

interface LogbookSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (logbookId: string) => void;
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const LogbookSelectionModal: React.FC<LogbookSelectionModalProps> = ({
    isOpen,
    onClose,
    onSelect,
}) => {
    const { tracklog } = useCurrentTracklog();
    const { isLogbookAssociated, suggestedItems, logbookEntries } =
        useCurrentLogbook();

    const matchedEntries = React.useMemo(() => {
        const associations = tracklog.logbookAssociationDetails || [];
        const sources = [...(logbookEntries || []), ...(suggestedItems || [])];

        return associations
            .map(a => sources.find(e => e.objectId === a.logbookEntryID))
            .filter(Boolean)
            .reverse();
    }, [logbookEntries, suggestedItems, tracklog.logbookAssociationDetails]);

    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="modal-title">
            <Box sx={modalStyle}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Typography variant="h6" component="h2" id="modal-title">
                        Select a Logbook Entry to View
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        maxHeight: 400,
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                    }}
                >
                    {matchedEntries.map(entry => {
                        const isAssociated = isLogbookAssociated(
                            entry.objectId
                        );

                        return (
                            <ListItem
                                key={entry.objectId}
                                sx={{
                                    borderBottom: '1px solid #e7e7e7',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                }}
                                onClick={() => onSelect(entry.objectId)}
                            >
                                <ListItemText>
                                    <Box display="flex" flexDirection="column">
                                        <LogbookEntryHeader
                                            logbookEntry={entry}
                                        />
                                        <LogbookEntryDetails
                                            logbookEntry={entry}
                                            isAssociated={isAssociated}
                                        />
                                    </Box>
                                </ListItemText>
                            </ListItem>
                        );
                    })}
                </Box>
            </Box>
        </Modal>
    );
};

export default LogbookSelectionModal;
